import axios from "axios";
import { ConfirmOptions } from "material-ui-confirm";
import { Buffer } from 'buffer';

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { IEmail } from "../types/base";
import { AppSettingService } from "./appSettingService";
import { IGeneratedDocument } from "../types/document";

export namespace UtilityService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Sort delegate for comparing two strings.
	 * @param a The first string to compare.
	 * @param b The second string to compare.
	 * @returns A negative number if a < b, a positive number if a > b, or 0 if they are equal.
	 **********/
	export function compareStrings(a: string | undefined, b: string | undefined) {
		let firstString: string = "";
		let secondString: string = "";

		if (a == undefined && b == undefined) return 0;
		if (a == undefined && b != undefined) return 1;
		if (a != undefined && b == undefined) return -1;

		if (a != undefined) firstString = a;
		if (b != undefined) secondString = b;

		let compare: number = 0;

		try {
			compare = firstString?.localeCompare(secondString);
		}
		catch (er: any) {
			let i = er.message
		}

		return compare;
	}

	/**
	 * Sort delegate for comparing two numbers.
	 * @param a The first number to compare.
	 * @param b The second number to compare.
	 * @returns A negative number if a < b, a positive number if a > b, or 0 if they are equal.
	 */
	export function compareNumbers(a: number | undefined, b: number | undefined) {
		if (a == undefined && b == undefined) return 0;
		else if (a == undefined && b != undefined) return 1;
		else if (a != undefined && b == undefined) return -1;
		else return a! - b!;
	}

	/**
	 * Sort delegate for comparing two booleans.
	 * @param a The first boolean to compare.
	 * @param b The second boolean to compare.
	 * @param trueBeforeFalse Whether true should be considered "less than" false. Undefined arguments are treated as false. Defaults to true.
	 * @returns A negative number if a < b, a positive number if a > b, or 0 if they are equal.
	 */
	export function compareBooleans(a: boolean | undefined, b: boolean | undefined, trueBeforeFalse: boolean = true) {
		if (a == undefined && b == undefined) return 0;
		else if (a == undefined && b != undefined) return compareBooleans(false, b, trueBeforeFalse);
		else if (a != undefined && b == undefined) return compareBooleans(a, false, trueBeforeFalse);
		else return (a === b) ? 0 : a ? (trueBeforeFalse ? -1 : 1) : (trueBeforeFalse ? 1 : -1);
	}

	/**
	 * Sort delegate for performing a primary and secondary comparison of two fields of an object.
	 * @param a The first object to compare.
	 * @param b The second object to compare.
	 * @param primaryField The name of the field for the primary comparison.
	 * @param secondaryField The name of the field for the secondary comparison.
	 * @param firstFieldType The data type of the first comparison ("string", "number", or "boolean"). Defaults to "string".
	 * @param secondFieldType The data type of the secondary comparison ("string", "number", or "boolean"). Defaults to "string".
	 * @param trueBeforeFalse Whether true should be considered "less than" false when comparing boolean fields. Undefined arguments are treated as false. Defaults to true.
	 * @returns A negative number if a < b, a positive number if a > b, or 0 if they are equal.
	 */
	export function compareTwoFields(a: any, b: any, primaryField: string, secondaryField: string, firstFieldType: "string" | "number" | "boolean" = "string", secondFieldType: "string" | "number" | "boolean" = "string", trueBeforeFalse: boolean = true) {
		let firstFieldResult: number;
		switch (firstFieldType) {
			case "string":
				firstFieldResult = compareStrings(a[primaryField], b[primaryField]);
				break;
			case "number":
				firstFieldResult = compareNumbers(a[primaryField], b[primaryField]);
				break;
			case "boolean":
				firstFieldResult = compareBooleans(a[primaryField], b[primaryField], trueBeforeFalse);
				break;
		}
		if (firstFieldResult != 0) {
			return firstFieldResult;
		}

		switch (secondFieldType) {
			case "string":
				return compareStrings(a[secondaryField], b[secondaryField]);
			case "number":
				return compareNumbers(a[secondaryField], b[secondaryField]);
			case "boolean":
				return compareBooleans(a[secondaryField], b[secondaryField], trueBeforeFalse);
		}
	}

	/**********
	 * Requests that the API send an email as a pre-configured mailbox.
	 * @param request The email.
	 * 
	 * DOESN'T WORK (yet)
	 * 
	 **********/
	export async function sendSystemEmailFromApi(request: IEmail) {
		const apiResponse = axios.put(
			apiConfig.default + "/utility/email",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Sends an email from the browser as the current user using Microsoft Graph API.
	 * @param request The email.
	 **********/
	export async function sendSystemEmailFromUi(request: IEmail) {
		const appSettings = AppSettingService.mapAppSettings((await AppSettingService.getAppSettings()).data);
		const subjectPreface = appSettings.find(s => s.name == "SystemEmailSubjectPreface")?.value || "HWI Online -";

		const email = {
			message: {
				subject: `${subjectPreface} ${request.subject}`,
				body: {
					contentType: 'Html',
					content: `<HTML><BODY>${request.body}</BODY></HTML>`,
				},
				toRecipients: [{ emailAddress: { address: request.to } }],
			}
		};

		try {
			// Get a Graph-specific token
			const graphToken = await credentialManager.getGraphToken();

			const apiResponse = await axios.post(
				"https://graph.microsoft.com/v1.0/me/sendMail",
				email,
				{
					headers: {
						'Authorization': `Bearer ${graphToken}`,
						'Content-Type': 'application/json'
					},
				}
			);
			return apiResponse;
		} catch (error) {
			console.error('Failed to send email:', error);
			throw error;
		}
	}

	/**********
	 * Shows a confirmation modal and returns the user's response.
	 * @param confirm A reference to the confirm function from material-ui-confirm's useConfirm(). Hooks must be called in a React component, so the confirm function must be passed in from one.
	 * @param title The title of the confirmation modal. Can be a string or React node.
	 * @param description The description text of the confirmation modal. Can be a string or React node.
	 * @param options Optional additional ConfirmationOptions for the confirmation modal. Overrides defaults.
	 * @returns A promise that resolves to true if the user confirmed, or false if they cancelled.
	 **********/
	export async function ConfirmationModal(confirm: Function, title: React.ReactNode, description: React.ReactNode, options?: ConfirmOptions): Promise<boolean> {
		const { confirmed, reason } = await confirm({
			...{
				title: title,
				description: description,
				confirmationText: 'Continue',
				confirmationButtonProps: { color: 'error', variant: 'contained' },
				cancellationText: 'Cancel',
				cancellationButtonProps: { color: 'primary', variant: 'outlined' },
				buttonOrder: ['confirm', 'cancel']
			},
			...options
		});
		return confirmed;
	}

	/**
	 * Finds an object property value by a case-insensitive key name in ES5 compatible JavaScript.
	 *
	 * @param object The object to search within.
	 * @param key The property name to search for (case -insensitive).
	 * @returns The value of the matching property, or undefined if not found.
	 */
	export function getParameterCaseInsensitive(object: Record<string, any>, key: string): any | undefined {
		if (!key) return undefined;
		const asLowercase = key.toLowerCase();

		// Get all property names (Object.keys is ES5 compatible)
		const keys = Object.keys(object);

		// Iterate over keys to find a match
		for (let i = 0; i < keys.length; i++) {
			const currentKey = keys[i];
			if (currentKey.toLowerCase() === asLowercase) {
				return object[currentKey];
			}
		}

		return undefined; // Return undefined if no match is found
	}

	/**
	 * Compare the contents of two record sets to determine if they are identical.
	 * @param a The first record set
	 * @param b The second record set
	 * @returns True if the contents of both record sets' key/value pairs are the same, or if both are undefined. False otherwise.
	 */
	export function areRecordContentsIdentical(a?: Record<string, string>, b?: Record<string, string>): boolean {
		if (!a || !b) return a === b; // (undefined === undefined) is also considered identical
		if (Object.keys(a).length !== Object.keys(b).length) return false;
		for (const key in a) {
			if (a[key] !== b[key]) return false;
		}
		return true;
	}

	/**
	 * Convert a base64-encoded data URL into an IGeneratedDocument with decoded contents.
	 * @param dataUrl The base64-encoded data URL to convert.
	 * @param filename The name of the file, as data URLs are nameless.
	 * @returns An IGenerated document object representing the decoded contents of the data URL.
	 */
	export function dataUrlToGeneratedDocument(dataUrl: string, filename: string = "FileFromDataUrl"): IGeneratedDocument {
		if (!dataUrl.startsWith("data:")) throw new Error("Invalid data URL format.");
		if (!dataUrl.includes(";base64")) throw new Error("Data URL is not a base64-encoded string.");
		const reducedString = dataUrl.replace("data:", "").replace(";base64,", ",");
		const [contentType, contents] = reducedString.split(",", 2);
		const document: IGeneratedDocument = {
			name: filename,
			contents: Buffer.from(contents, 'base64'),
			contentType: contentType
		};

		return document;
	}

	/**
	 * Convert an IGeneratedDocument into a base64-encoded data URL. The filename will be discarded, as data URLs are nameless.
	 * @param document The IGeneratedDocument to convert.
	 * @returns A base64-encoded data URL representing the document's contents.
	 */
	export function generatedDocumentToDataUrl(document: IGeneratedDocument): string {
		if (!document?.contentType) throw new Error("Document does not have a contentType.");
		if (!document?.contents) throw new Error("Document does not have contents.");
		const base64Contents = document.contents.toString('base64');
		const dataUrl = `data:${document.contentType};base64,${base64Contents}`;
		return dataUrl;
	}

	/**
	 * Takes an IGeneratedDocument object and triggers a download in the browser.
	 * @param file The IGeneratedDocument to download.
	 */
	export const downloadGeneratedDocument = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	/**
	 * Attempt to convert a plural word into its singular form. This can produce some very stupid things so use with caution.
	 * @param word The plural word to convert.
	 * @returns The singular form of the word.
	 */
	export const singularize = (word: string): string => {
		if (!word || word.length === 0) return word;
		if (word.endsWith("ies")) {
			return word.slice(0, -3) + "y";
		} else if (word.endsWith("s") && !word.endsWith("ss")) {
			return word.slice(0, -1);
		}
		return word;
	}
}